'use client';

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { NextIntlClientProvider } from 'next-intl';
import { type Locale, defaultLocale, getMessages, localeMetadata } from './config';
import { captureError, buildTags, toSerializable } from '@/lib/monitoring/logger';

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => Promise<boolean>;
  loading: boolean;
}

const LocaleContext = createContext<LocaleContextType | null>(null);

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return context;
}

interface LocaleProviderProps {
  children: React.ReactNode;
  tenantId?: string;
  initialLocale?: Locale;
  initialMessages?: Record<string, unknown>;
}

export function LocaleProvider({ 
  children, 
  tenantId,
  initialLocale = defaultLocale,
  initialMessages
}: LocaleProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const [messages, setMessages] = useState<Record<string, unknown>>(initialMessages ?? {});
  const [loading, setLoading] = useState(false);

  const initialLocaleRef = useRef(initialLocale);
  const skipInitialLoadRef = useRef(Boolean(initialMessages && Object.keys(initialMessages).length > 0));

  const latestLocaleRef = useRef(locale);
  latestLocaleRef.current = locale;

  const ensureCookie = (value: Locale) => {
    if (typeof document !== 'undefined') {
      document.cookie = `preferred_locale=${value}; path=/; max-age=31536000; SameSite=Lax`;
    }
  };

  const loadMessages = useCallback(async (targetLocale: Locale): Promise<boolean> => {
    try {
      const loadedMessages = await getMessages(targetLocale);
      setMessages(loadedMessages);
      return true;
    } catch (error) {
      captureError(error, {
        tags: buildTags('i18n-client-provider', 'load-messages'),
        extra: toSerializable({ locale: targetLocale })
      });
      if (targetLocale !== defaultLocale) {
        try {
          const fallbackMessages = await getMessages(defaultLocale);
          setMessages(fallbackMessages);
        } catch (fallbackError) {
          captureError(fallbackError, {
            tags: buildTags('i18n-client-provider', 'load-fallback'),
            extra: toSerializable({ fallbackLocale: defaultLocale })
          });
        }
      }
      return false;
    }
  }, []);

  useEffect(() => {
    if (!skipInitialLoadRef.current) {
      setLoading(true);
      loadMessages(locale).finally(() => setLoading(false));
    }
    skipInitialLoadRef.current = false;
  }, [locale, loadMessages]);

  useEffect(() => {
    ensureCookie(locale);
  }, [locale]);

  useEffect(() => {
    if (initialLocale !== initialLocaleRef.current) {
      initialLocaleRef.current = initialLocale;
      skipInitialLoadRef.current = true;
      setLocaleState(initialLocale);
      ensureCookie(initialLocale);
    }
  }, [initialLocale]);

  useEffect(() => {
    if (initialMessages && Object.keys(initialMessages).length > 0) {
      skipInitialLoadRef.current = true;
      setMessages(initialMessages);
    }
  }, [initialMessages]);

  const setLocale = async (newLocale: Locale): Promise<boolean> => {
    if (newLocale === latestLocaleRef.current) {
      return true;
    }

    const previousLocale = latestLocaleRef.current;
    skipInitialLoadRef.current = true;
    setLocaleState(newLocale);
    setLoading(true);

    try {
      if (tenantId) {
        const response = await fetch('/api/tenant/locale', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ locale: newLocale })
        });

        if (!response.ok) {
          const errorPayload = await response.json().catch(() => ({}));
          throw new Error(errorPayload.error || 'Failed to update tenant locale');
        }
      }

      const loaded = await loadMessages(newLocale);
      if (!loaded) {
        throw new Error('Failed to load messages for selected locale');
      }

      ensureCookie(newLocale);
      return true;
    } catch (error) {
      captureError(error, {
        tags: buildTags('i18n-client-provider', 'switch-locale'),
        extra: toSerializable({ locale })
      });
      skipInitialLoadRef.current = true;
      setLocaleState(previousLocale);
      await loadMessages(previousLocale);
      ensureCookie(previousLocale);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const contextValue: LocaleContextType = {
    locale,
    setLocale,
    loading
  };

  const timeZone = localeMetadata[locale]?.timezone ?? 'Europe/London';

  return (
    <LocaleContext.Provider value={contextValue}>
      <NextIntlClientProvider locale={locale} messages={messages} timeZone={timeZone}>
        {children}
      </NextIntlClientProvider>
    </LocaleContext.Provider>
  );
}
